import { Route, NavigationGuardNext } from 'vue-router';
import store from '@/store';
import trackSeekerAccountCreation from '@/lib/tracking/segment_track_basic';
import { getSharedApolloClient } from '@/lib/apollo_setup';
import { fetchActiveScCareSessions } from '@/helpers/scCareSessionHelper';
import { isOn } from '@/lib/growthbook';

const SESSION_STORAGE_KEY = 'corporate_onboarding_segment_events_fired';
const CORPORATE_ONBOARDING_NEW_FLAG = 'corporate-onboarding-new';
const GUIDED_PATH = '/corporate-onboarding/guided';
const CARE_TYPE_PATH = '/corporate-onboarding/care-type';
const BACKUP_CARE_PATH = '/corporate-onboarding/backup-care';

function isCorporateOnboardingIndex(path: string): boolean {
  return path === '/corporate-onboarding' || path === '/corporate-onboarding/';
}

function isGuidedRoute(path: string): boolean {
  return path === GUIDED_PATH || path.startsWith(`${GUIDED_PATH}/`);
}

export function trackCorporateOnboardingSegment(): void {
  try {
    const account = store.getters['account/getAccount'];

    if (!account) {
      return;
    }

    const hasFiredEvents = sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';

    if (!hasFiredEvents) {
      const accountProperties = {
        firstName: account.firstName,
        lastName: account.lastName,
        email: account.email,
        familyType: account.accountType(),
      };

      trackSeekerAccountCreation(accountProperties);
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    }
  } catch (error) {
    console.error('Error in trackCorporateOnboardingSegment:', error);
  }
}

/**
 * Resolves corporate onboarding entry. BUC users (active SC care sessions) always get backup-care
 * and never enter the guided experiment — including when external signup lands on the index path.
 * Non-BUC users follow the corporate-onboarding-new experiment (guided) or control (care-type).
 */
export async function resolveCorporateOnboardingFlow(to: Route, _from: Route, next: NavigationGuardNext): Promise<void> {
  const isIndexRoute = isCorporateOnboardingIndex(to.path);
  const isCareTypeRoute = to.path === CARE_TYPE_PATH;
  const isBackupCareRoute = to.path === BACKUP_CARE_PATH;
  const isGuidedOnboardingRoute = isGuidedRoute(to.path);

  if (!isIndexRoute && !isCareTypeRoute && !isBackupCareRoute && !isGuidedOnboardingRoute) {
    next();
    return;
  }

  const isNewOnboardingEnabled = isOn(CORPORATE_ONBOARDING_NEW_FLAG);

  if (to.query.skip_backup_care === 'true') {
    if (isGuidedOnboardingRoute) {
      if (isNewOnboardingEnabled) {
        next();
        return;
      }
      next({ path: CARE_TYPE_PATH, query: to.query, replace: true });
      return;
    }

    if (isIndexRoute) {
      next({
        path: isNewOnboardingEnabled ? GUIDED_PATH : CARE_TYPE_PATH,
        query: to.query,
        replace: true,
      });
      return;
    }

    if (isBackupCareRoute) {
      next({ path: CARE_TYPE_PATH, query: to.query, replace: true });
      return;
    }

    next();
    return;
  }

  try {
    const apolloClient = getSharedApolloClient();
    const activeSessions = await fetchActiveScCareSessions(apolloClient);
    const hasActiveBackupCare = activeSessions.length > 0;

    // BUC always wins: never send active backup-care users into the guided experiment.
    if (hasActiveBackupCare) {
      if (isBackupCareRoute) {
        next();
        return;
      }

      next({ path: BACKUP_CARE_PATH, query: to.query, replace: true });
      return;
    }

    if (isNewOnboardingEnabled) {
      if (isIndexRoute) {
        next({ path: GUIDED_PATH, query: to.query, replace: true });
        return;
      }

      if (isBackupCareRoute) {
        next({ path: CARE_TYPE_PATH, query: to.query, replace: true });
        return;
      }

      // Care-type and guided remain reachable for non-BUC experiment users.
      next();
      return;
    }

    if (isGuidedOnboardingRoute || isIndexRoute || isBackupCareRoute) {
      next({ path: CARE_TYPE_PATH, query: to.query, replace: true });
      return;
    }

    next();
  } catch (error) {
    console.error('Error resolving corporate onboarding flow:', error);

    if (isIndexRoute || isBackupCareRoute || isGuidedOnboardingRoute) {
      next({
        path: CARE_TYPE_PATH,
        query: { ...to.query, skip_backup_care: 'true' },
        replace: true,
      });
      return;
    }

    next();
  }
}

const corporateOnboardingGuard = async (to: Route, from: Route, next: NavigationGuardNext): Promise<void> => {
  trackCorporateOnboardingSegment();
  await resolveCorporateOnboardingFlow(to, from, next);
};

export default corporateOnboardingGuard;
